import { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, Settings, LogOut, MessageCircle, Sparkles, Building2, Banknote } from 'lucide-react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { orderedNavItems } from './data';
import { ROUTES } from '../../config/constants';
import dayjs from 'dayjs';
import { getUserDisplayName, getUserInitials as getInitials } from '../../utils/userUtils';
import { getTokenPayload } from '../../utils/jwtUtils';

const SidebarDropdown = ({ menu, isExpanded, onNavigate, onExpandSidebar, openDropdownId, setOpenDropdownId, onCancelMouseLeave }) => {
  const location = useLocation();
  const isOpen = openDropdownId === menu.id;
  // Check if any dropdown item is active, handling query parameters
  const isActive = menu.items?.some(item => {
    const itemPathname = item.path.split('?')[0];
    const itemSearch = item.path.includes('?') ? `?${item.path.split('?')[1]}` : '';
    // Require exact match of both pathname and search (including when both are empty)
    return location.pathname === itemPathname && location.search === itemSearch;
  });

  const handleToggle = () => {
    // Cancel any pending mouse-leave collapse so the sidebar stays open
    onCancelMouseLeave?.();
    if (!isExpanded) {
      // If collapsed, expand sidebar and open this dropdown
      onExpandSidebar();
      setOpenDropdownId(menu.id);
    } else {
      // If expanded, just toggle the dropdown
      setOpenDropdownId(isOpen ? null : menu.id);
    }
  };

  // Close dropdown when sidebar collapses
  useEffect(() => {
    if (!isExpanded) {
      setOpenDropdownId(null);
    }
  }, [isExpanded, setOpenDropdownId]);

  if (!isExpanded) {
    // Collapsed state - show icon that expands sidebar on click
    return (
      <div className="relative group">
        <button
          onClick={(e) => { e.stopPropagation(); handleToggle(); }}
          className={clsx(
            "flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors cursor-pointer",
            isActive
              ? "bg-gray-200 text-black"
              : "text-gray-500 hover:bg-gray-100 hover:text-black"
          )}
          title={menu.label}
        >
          <menu.icon size={20} />
        </button>
        {/* Tooltip */}
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
          {menu.label}
        </div>
      </div>
    );
  }

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        onClick={(e) => { e.stopPropagation(); handleToggle(); }}
        className={clsx(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer",
          isActive
            ? "bg-gray-200 text-black"
            : "text-gray-500 hover:bg-gray-100 hover:text-black"
        )}
      >
        <menu.icon size={20} />
        <span className="flex-1 text-left truncate text-gray-600">{menu.label}</span>
        <ChevronDown
          size={16}
          className={clsx(
            "transition-transform duration-200 text-gray-500",
            isOpen && "rotate-180"
          )}
        />
      </button>

      {isOpen && (
        <div className="mt-1 ml-5 pl-3 border-l border-gray-400 space-y-1">
          {menu.items?.map((item) => {
            // For paths with query strings, manually match against full location
            const itemPathname = item.path.split('?')[0];
            const itemSearch = item.path.includes('?') ? `?${item.path.split('?')[1]}` : '';
            // Require exact match of both pathname and search (including when both are empty)
            const isItemActive =
              location.pathname === itemPathname &&
              location.search === itemSearch;
            return (
              <NavLink
                key={item.id}
                to={item.path}
                end
                onClick={(e) => { e.stopPropagation(); }}
                className={clsx(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                  isItemActive
                    ? "bg-gray-200 text-black font-medium"
                    : "text-gray-600 hover:bg-gray-100 hover:text-black"
                )}
              >
                <item.icon size={16} className="text-gray-500" />
                <span className="truncate">{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};

const SidebarNavLink = ({ item, isExpanded, onNavigate }) => {
  const location = useLocation();
  // For paths with query strings, match both pathname and search
  const itemPathname = item.path.split('?')[0];
  const itemSearch = item.path.includes('?') ? `?${item.path.split('?')[1]}` : '';
  // Require exact match of both pathname and search (including when both are empty)
  const isActive = location.pathname === itemPathname && location.search === itemSearch;

  const handleClick = (e) => {
    e.stopPropagation();
    if (item.isNeurolawResearch) {
      e.preventDefault();
      onNavigate(item.path, true);
    }
  };

  if (!isExpanded) {
    return (
      <div className="relative group">
        <NavLink
          to={item.path}
          onClick={handleClick}
          className={clsx(
            "flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors cursor-pointer",
            isActive
              ? "bg-gray-200 text-black"
              : "text-gray-500 hover:bg-gray-100 hover:text-black"
          )}
          title={item.label}
        >
          <item.icon size={20} />
        </NavLink>
        {/* Tooltip */}
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
          {item.label}
        </div>
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      onClick={handleClick}
      className={clsx(
        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors cursor-pointer",
        isActive
          ? "bg-gray-200 text-black"
          : "text-gray-500 hover:bg-gray-100 hover:text-black"
      )}
    >
      <item.icon size={20} />
      <span className="truncate text-gray-600">{item.label}</span>
    </NavLink>
  );
};



const DesktopSidebar = ({ isExpanded, toggleSidebar, onNavigate, user, userActions, onOpenSupport }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [isProfilePopupOpen, setIsProfilePopupOpen] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const profilePopupRef = useRef(null);
  const profileHoverTimeoutRef = useRef(null);

  // Redux state for live credits and points
  const reduxCredits = useSelector((state) => state.credits?.value ?? 0);
  const reduxPoints = useSelector((state) => state.points?.value ?? 0);

  // Check if user is org member from JWT
  const isOrgMember = (() => {
    const payload = getTokenPayload();
    if (!payload) return false;
    return payload.isOrgUser === true && payload.accountType !== 'organization';
  })();

  const [showAssistant, setShowAssistant] = useState(() => {
    try {
      const saved = localStorage.getItem('showAssistant');
      return saved !== null ? JSON.parse(saved) : false;
    } catch (e) {
      return false;
    }
  });

  const getUserInitials = () => {
    return getInitials(user);
  };

  const handleExpandSidebar = () => {
    if (!isExpanded) {
      toggleSidebar();
    }
  };

  const handleCollapseSidebar = () => {
    if (isExpanded) {
      // Small delay for smooth transition after navigation
      setTimeout(() => {
        toggleSidebar();
      }, 150);
    }
  };

  // Handle hover to expand sidebar and auto-collapse on mouse leave
  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(true);
    if (!isExpanded) {
      toggleSidebar();
    }
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovered(false);
    // Auto-collapse sidebar when mouse leaves, but only if no dropdown is open
    if (isExpanded && !openDropdownId) {
      // Use shorter delay for immediate response
      hoverTimeoutRef.current = setTimeout(() => {
        toggleSidebar();
        setOpenDropdownId(null); // Close any open dropdowns
      }, 100);
    }
  };

  // Cancel any pending mouse-leave collapse (called when a dropdown is opened)
  const handleCancelMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const handleProfileMouseEnter = () => {
    if (profileHoverTimeoutRef.current) clearTimeout(profileHoverTimeoutRef.current);
    setIsProfilePopupOpen(true);
  };

  const handleProfileMouseLeave = () => {
    if (profileHoverTimeoutRef.current) clearTimeout(profileHoverTimeoutRef.current);
    profileHoverTimeoutRef.current = setTimeout(() => setIsProfilePopupOpen(false), 250);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
      if (profileHoverTimeoutRef.current) {
        clearTimeout(profileHoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <aside
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={clsx(
        "hidden lg:flex fixed top-0 left-0 h-screen bg-gray-100 border-r border-gray-400 flex-col transition-all duration-300 ease-in-out",
        isExpanded ? "w-60" : "w-16"
      )}
      style={{ zIndex: 1000 }}
    >
      {/* Logo Section with Settings Icon */}
      <div
        className={clsx(
          "flex items-center h-16 flex-shrink-0 border-b border-gray-400",
          isExpanded ? "px-2 justify-between gap-2" : "justify-center"
        )}
      >
        {isExpanded ? (
          <>

            {/* Logo and Text */}
            <Link
              to={ROUTES.HOME}
              className="flex items-center gap-2 group min-w-0 cursor-pointer flex-1"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-8 h-8 flex items-center justify-center rounded-lg text-white shadow-sm group-hover:scale-105 transition-transform flex-shrink-0">
                <img
                  src="/logo.webp"
                  alt="Neurolaw AI Logo"
                  className="w-full h-full object-contain"
                  style={{ mixBlendMode: "multiply" }}
                />
              </div>
              <div className="min-w-0">
                <h1 className="text-sm font-bold text-black truncate">
                  Neurolaw AI
                </h1>
              </div>
            </Link>
          </>
        ) : (
          // Collapsed - Show only Logo
          <Link
            to={ROUTES.HOME}
            className="flex items-center justify-center group cursor-pointer"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-lg text-white group-hover:scale-105 transition-transform">
              <img
                src="/logo.webp"
                alt="Neurolaw AI Logo"
                className="w-full h-full object-contain"
                style={{ mixBlendMode: "multiply" }}
              />
            </div>
          </Link>
        )}
      </div>

      {/* Toggle Button */}
      <div className="px-2 py-1.5 flex-shrink-0">
        <button
          onClick={(e) => { e.stopPropagation(); toggleSidebar(); }}
          className={clsx(
            "flex items-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-black transition-colors cursor-pointer",
            isExpanded ? "w-full gap-2 px-3 py-2" : "w-10 h-10 mx-auto justify-center"
          )}
          title={isExpanded ? "Collapse sidebar" : "Expand sidebar"}
        >
          {isExpanded ? (
            <>
              <ChevronLeft size={18} />
              <span className="text-sm text-gray-600">Collapse</span>
            </>
          ) : (
            <ChevronRight size={18} />
          )}
        </button>
      </div>

      {/* Horizontal Line */}
      <div className="border-t border-gray-400" />

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2 space-y-1 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent hover:scrollbar-thumb-gray-500">
        {orderedNavItems.map((navItem) => {
          if (navItem.type === 'link') {
            return (
              <SidebarNavLink
                key={navItem.item.id}
                item={navItem.item}
                isExpanded={isExpanded}
                onNavigate={onNavigate}
              />
            );
          } else if (navItem.type === 'dropdown') {
            return (
              <SidebarDropdown
                key={navItem.menu.id}
                menu={navItem.menu}
                isExpanded={isExpanded}
                onNavigate={onNavigate}
                onExpandSidebar={handleExpandSidebar}
                openDropdownId={openDropdownId}
                setOpenDropdownId={setOpenDropdownId}
                onCancelMouseLeave={handleCancelMouseLeave}
              />
            );
          }
          return null;
        })}
      </nav>

      {/* Bottom Section - User Profile with Popup */}
      <div className="border-t border-gray-400 p-2 flex-shrink-0">

        {/* Credits & Points Display */}
        {!isExpanded ? (
          /* Collapsed: stacked icons above profile */
          <></>
          // <div className="flex flex-col items-center gap-1.5 mb-2">
          //   <div className="relative group">
          //     <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-green-50 hover:bg-green-100 transition-colors">
          //       <Banknote size={18} className="text-green-600" />
          //     </div>
          //     <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
          //       Credits: {Math.floor(reduxCredits).toLocaleString()}
          //     </div>
          //   </div>
          //   <div className="relative group">
          //     <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors">
          //       <img src="/n-coin.webp" alt="Coins" className="w-5 h-5" />
          //     </div>
          //     <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all whitespace-nowrap z-50">
          //       Points: {Math.floor(reduxPoints).toLocaleString()}
          //     </div>
          //   </div>
          // </div>
        ) : (
          /* Expanded: full row display above plan details */
          // <div className="flex items-center gap-2 px-2 py-1.5 mb-2">
          //   <div className="flex items-center gap-1.5 flex-1 bg-green-50 rounded-lg px-2.5 py-1.5">
          //     <Banknote size={15} className="text-green-600 flex-shrink-0" />
          //     <span className="text-xs font-medium text-green-700 truncate">{Math.floor(reduxCredits).toLocaleString()}</span>
          //   </div>
          //   <div className="flex items-center gap-1.5 flex-1 bg-amber-50 rounded-lg px-2.5 py-1.5">
          //     <img src="/n-coin.webp" alt="Coins" className="w-4 h-4 flex-shrink-0" />
          //     <span className="text-xs font-medium text-amber-600 truncate">{Math.floor(reduxPoints).toLocaleString()}</span>
          //   </div>
          // </div>
          <></>
        )}

        {/* Plan Title */}
        {isExpanded && user?.subscriptionType === 'plan' && user?.expiryTime && (
          <div className="px-2 py-1.5 mb-2">
            <p className="text-xs font-semibold text-gray-900 mb-0.5">Paid Plan</p>
            <p className="text-xs text-gray-500">Until {dayjs(user.expiryTime).format('MMM DD, YYYY')}</p>
          </div>
        )}
        
        <div className="relative" ref={profilePopupRef} onMouseEnter={handleProfileMouseEnter} onMouseLeave={handleProfileMouseLeave}>
          {/* User Profile Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsProfilePopupOpen(!isProfilePopupOpen);
            }}
            className={clsx(
              "flex items-center rounded-lg transition-colors hover:bg-gray-100 cursor-pointer w-full",
              isExpanded ? "gap-3 p-2" : "justify-center p-2"
            )}
          >
            <div className={clsx(
              "w-8 h-8 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0",
              user?.subscriptionType === 'plan' && "ring-2 ring-yellow-500 ring-offset-2",
              isOrgMember && "ring-2 ring-purple-500 ring-offset-2"
            )}>
              {user?.profilePicture_url ? (
                <img src={user.profilePicture_url} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                getInitials(user)
              )}
            </div>
            {isExpanded && (
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <p className="text-sm font-medium text-black truncate text-left">{getUserDisplayName(user).name || 'User'}</p>
                  {getUserDisplayName(user).isOrganization && (
                    <Building2 size={12} className="text-gray-500 flex-shrink-0" />
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate text-left">{user?.email || ''}</p>
              </div>
            )}
          </button>

          {/* Profile Popup Menu */}
          {isProfilePopupOpen && (
            <>
              {/* Backdrop to close on click outside */}
              <div
                className="fixed inset-0 z-[9998]"
                onClick={() => setIsProfilePopupOpen(false)}
              />

              {/* Popup */}
              <div className={clsx(
                "absolute bottom-full mb-2 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-[9999]",
                isExpanded ? "left-0 right-0" : "left-0 w-60"
              )}>
                {/* User Info */}
                <div
                  className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    userActions.Profile();
                    setIsProfilePopupOpen(false);
                  }}
                >
                  <div className="flex items-center gap-3">
                    <div className={clsx(
                      "w-10 h-10 rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0",
                      user?.subscriptionType === 'plan' && "ring-2 ring-yellow-500 ring-offset-2",
                      isOrgMember && "ring-2 ring-purple-500 ring-offset-2"
                    )}>
                      {user?.profilePicture_url ? (
                        <img src={user.profilePicture_url} alt={getUserDisplayName(user).name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        getInitials(user)
                      )}
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <div className="flex items-center gap-1">
                        <p className="text-sm font-semibold text-gray-900 truncate text-left">{getUserDisplayName(user).name || 'User'}</p>
                        {getUserDisplayName(user).isOrganization && (
                          <Building2 size={14} className="text-gray-500 flex-shrink-0" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate text-left">{user?.email || ''}</p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  {/* AI Assistant Toggle */}
                  <div className="px-4 py-3 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center gap-3">
                      <Sparkles size={18} className="text-gray-600 flex-shrink-0" />
                      <span className="text-sm text-gray-700 flex-1">AI Assistant</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAssistant(prev => !prev);
                          localStorage.setItem('showAssistant', JSON.stringify(!showAssistant));
                          window.dispatchEvent(new CustomEvent('toggleAssistant'));
                        }}
                        className={clsx(
                          "w-10 h-5 rounded-full transition-all duration-300 p-0.5 flex-shrink-0",
                          showAssistant ? "bg-gray-900" : "bg-gray-300"
                        )}
                      >
                        <div className={clsx(
                          "w-4 h-4 rounded-full bg-white shadow-md transition-transform duration-300",
                          showAssistant ? "translate-x-5" : "translate-x-0"
                        )} />
                      </button>
                    </div>
                  </div>

                  {/* Support */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onOpenSupport) onOpenSupport();
                      setIsProfilePopupOpen(false);
                    }}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <MessageCircle size={18} className="text-gray-600 flex-shrink-0" />
                    <span className="text-sm text-gray-700">Support</span>
                  </button>

                  {/* Settings */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(ROUTES.SETTINGS);
                      setIsProfilePopupOpen(false);
                    }}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <Settings size={18} className="text-gray-600 flex-shrink-0" />
                    <span className="text-sm text-gray-700">Settings</span>
                  </button>

                  {/* Logout */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      userActions.logout();
                      setIsProfilePopupOpen(false);
                    }}
                    className="w-full px-4 py-3 flex items-center gap-3 hover:bg-red-50 transition-colors text-left border-t border-gray-200"
                  >
                    <LogOut size={18} className="text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-500">Logout</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default DesktopSidebar;
